import { useState, useEffect } from "react";

// ── Brand tokens ─────────────────────────────────────────────
const BRAND = {
  navy:    "#0a2a4a",
  navyMid: "#0d3d5c",
  ocean:   "#0a7a8a",
  aqua:    "#7fffd4",
  gold:    "#FFD700",
  coral:   "#FF6B4A",
  white:   "#ffffff",
  glass:   "rgba(255,255,255,0.07)",
  glassBorder: "rgba(255,255,255,0.13)",
};

// ── Logo mark SVG ────────────────────────────────────────────
function LogoMark({ size = 36 }) {
  const s = size / 44;
  return (
    <svg width={size} height={size} viewBox="0 0 44 44" fill="none">
      <circle cx="22" cy="20" r="13" fill={BRAND.gold}/>
      <path d="M9 26 L18 26 L18 18 L35 18" stroke={BRAND.aqua} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="9" y="31" width="26" height="2.5" rx="1.25" fill={BRAND.coral}/>
      <line x1="22" y1="4" x2="22" y2="1" stroke={BRAND.gold} strokeWidth="2" strokeLinecap="round"/>
      <line x1="33" y1="8" x2="35" y2="6" stroke={BRAND.gold} strokeWidth="2" strokeLinecap="round"/>
      <line x1="11" y1="8" x2="9" y2="6" stroke={BRAND.gold} strokeWidth="2" strokeLinecap="round"/>
      <line x1="37" y1="20" x2="40" y2="20" stroke={BRAND.gold} strokeWidth="2" strokeLinecap="round"/>
      <line x1="7" y1="20" x2="4" y2="20" stroke={BRAND.gold} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

// ── Weekly Habits ────────────────────────────────────────────
const WEEKLY_HABITS = [
  { id: "h1", label: "Cooked at home",    emoji: "🍳", bonus: 3 },
  { id: "h2", label: "Skipped coffee shop",emoji: "☕", bonus: 2 },
  { id: "h3", label: "10k steps",          emoji: "👟", bonus: 2 },
  { id: "h4", label: "No impulse buys",    emoji: "🛍️", bonus: 3 },
  { id: "h5", label: "Packed lunch",       emoji: "🥙", bonus: 2 },
  { id: "h6", label: "No DoorDash",        emoji: "🍔", bonus: 3 },
  { id: "h7", label: "Happy hour at home", emoji: "🍷", bonus: 2 },
];

const SWAP_OPTIONS = [
  { label: "DoorDash skipped",        emoji: "🍔", amount: 32 },
  { label: "Coffee shop pass",        emoji: "☕", amount: 7  },
  { label: "Impulse Amazon buy",      emoji: "📦", amount: 45 },
  { label: "Happy hour skipped",      emoji: "🍹", amount: 28 },
  { label: "Lunch out skipped",       emoji: "🥗", amount: 18 },
  { label: "Streaming downgrade",     emoji: "📺", amount: 6  },
  { label: "Manicure at home",        emoji: "💅", amount: 55 },
  { label: "Gas station snacks",      emoji: "🍫", amount: 9  },
  { label: "Rideshare upgrade skipped",emoji: "🚗", amount: 14 },
  { label: "Wine bottle in",          emoji: "🍷", amount: 22 },
];

const GOAL_PRESETS = [
  { type: "destination", label: "Destination", emoji: "🌴",
    options: ["Tulum","Amalfi Coast","Bali","Maldives","Cabo","Paris","Tokyo","Santorini","Iceland","Costa Rica"] },
  { type: "event", label: "Event", emoji: "🎉",
    options: ["Girls Weekend","Couples Getaway","Anniversary Trip","Birthday Trip","Family Vacation","Bachelorette","Holiday Trip","Solo Reset","Spa Weekend","Adventure Trip"] },
];

const DEFAULT_GOALS = [
  { id: "g1", name: "Girls Weekend", type: "event",       emoji: "👯", goal: 3200, saved: 2340, color: BRAND.coral },
  { id: "g2", name: "Tulum",         type: "destination", emoji: "🌴", goal: 4500, saved: 800,  color: BRAND.aqua  },
];

const FRIENDS = [
  { name: "Jasmine", avatar: "🌸", saved: 1840, goal: 4500, streak: 12, event: "Tulum" },
  { name: "Renee",   avatar: "✨", saved: 3100, goal: 6200, streak: 8,  event: "Amalfi Coast" },
  { name: "Tara",    avatar: "🌙", saved: 920,  goal: 3800, streak: 3,  event: "Bali" },
  { name: "Kelsey",  avatar: "🔥", saved: 4200, goal: 4500, streak: 21, event: "Cabo" },
];

const BADGES = [
  { id: "first",   label: "First Step",    emoji: "👣", earned: true  },
  { id: "streak7", label: "Week Warrior",  emoji: "🔥", earned: true  },
  { id: "skip10",  label: "Skip Master",   emoji: "🚫", earned: true  },
  { id: "500",     label: "$500 Club",     emoji: "💰", earned: true  },
  { id: "social",  label: "Squad Goals",   emoji: "👯", earned: false },
  { id: "halfway", label: "Halfway There", emoji: "🌊", earned: false },
  { id: "dollarq", label: "Dollar Queen",  emoji: "👑", earned: false },
];

// ── Background ───────────────────────────────────────────────
function WaveBg() {
  return (
    <div style={{ position:"fixed", inset:0, zIndex:0, overflow:"hidden",
      background:`linear-gradient(160deg, ${BRAND.navy} 0%, #0d4a6a 40%, ${BRAND.ocean} 75%, #0d6b5e 100%)` }}>
      <svg style={{position:"absolute",bottom:0,left:0,width:"100%",opacity:0.15}} viewBox="0 0 1440 320" preserveAspectRatio="none">
        <path fill={BRAND.aqua} d="M0,192L48,186.7C96,181,192,171,288,176C384,181,480,203,576,197.3C672,192,768,160,864,149.3C960,139,1056,149,1152,165.3C1248,181,1344,203,1392,213.3L1440,224L1440,320L0,320Z"/>
      </svg>
      <svg style={{position:"absolute",bottom:0,left:0,width:"100%",opacity:0.08}} viewBox="0 0 1440 320" preserveAspectRatio="none">
        <path fill={BRAND.coral} d="M0,256L60,245.3C120,235,240,213,360,218.7C480,224,600,256,720,261.3C840,267,960,245,1080,224C1200,203,1320,181,1380,170.7L1440,160L1440,320L0,320Z"/>
      </svg>
      {[...Array(20)].map((_,i)=>(
        <div key={i} style={{
          position:"absolute", borderRadius:"50%", background:"rgba(255,255,200,0.75)",
          width:`${Math.random()*2.5+0.5}px`, height:`${Math.random()*2.5+0.5}px`,
          left:`${Math.random()*100}%`, top:`${Math.random()*65}%`,
          animation:`twinkle ${2+Math.random()*3}s ease-in-out infinite`,
          animationDelay:`${Math.random()*4}s`
        }}/>
      ))}
    </div>
  );
}

// ── Progress Ring ────────────────────────────────────────────
function ProgressRing({ pct, size=120, stroke=11, color=BRAND.aqua, children }) {
  const r = (size-stroke)/2, circ = 2*Math.PI*r, offset = circ-(pct/100)*circ;
  return (
    <svg width={size} height={size} style={{transform:"rotate(-90deg)"}}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={stroke}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color}
        strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" style={{transition:"stroke-dashoffset 1s ease"}}/>
      <foreignObject x={stroke} y={stroke} width={size-stroke*2} height={size-stroke*2}>
        <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",
          justifyContent:"center",transform:"rotate(90deg)",flexDirection:"column"}}>
          {children}
        </div>
      </foreignObject>
    </svg>
  );
}

// ── Swap FX ──────────────────────────────────────────────────
function SwapFx({ amount, onDone }) {
  const [phase, setPhase] = useState(0);
  useEffect(()=>{
    const t1=setTimeout(()=>setPhase(1),100);
    const t2=setTimeout(()=>setPhase(2),1100);
    const t3=setTimeout(()=>onDone(),1800);
    return ()=>{clearTimeout(t1);clearTimeout(t2);clearTimeout(t3);};
  },[]);
  return (
    <div style={{position:"fixed",inset:0,zIndex:200,display:"flex",alignItems:"center",
      justifyContent:"center",background:"rgba(0,20,40,0.9)",
      transition:"opacity 0.4s",opacity:phase===2?0:1}}>
      <div style={{textAlign:"center",
        transform:phase===1?"scale(1)":"scale(0.75)",
        transition:"transform 0.5s cubic-bezier(.34,1.56,.64,1)"}}>
        <div style={{fontSize:64,marginBottom:8}}>✈️</div>
        <div style={{fontSize:40,fontWeight:900,color:BRAND.aqua,
          fontFamily:"'Playfair Display',serif"}}>+${Number(amount).toFixed(0)}</div>
        <div style={{color:"rgba(255,255,255,0.6)",fontSize:14,marginTop:6,fontFamily:"Lato,sans-serif"}}>
          added to your fund!
        </div>
        <div style={{marginTop:16,fontSize:24}}>🌴 🏖️ 🌴</div>
        {/* Coral accent bar */}
        <div style={{width:80,height:3,background:BRAND.coral,borderRadius:2,margin:"16px auto 0"}}/>
      </div>
    </div>
  );
}

// ── Add Goal Modal ───────────────────────────────────────────
function AddGoalModal({ onAdd, onClose }) {
  const [step, setStep]       = useState(1);
  const [gType, setGType]     = useState(null);
  const [gName, setGName]     = useState("");
  const [gEmoji, setGEmoji]   = useState("🎯");
  const [gGoal, setGGoal]     = useState("");
  const [customName, setCustomName] = useState("");

  const EMOJIS = ["🌴","🏝️","🎉","👯","💑","🎂","🌺","✈️","🏖️","🎊","🌊","🦋","🍾","🌙","🔥","💫","🌎","🎯"];

  function finish() {
    const n = parseFloat(gGoal);
    if (!n || !gName) return;
    onAdd({ id: Date.now().toString(), name: gName, type: gType, emoji: gEmoji,
      goal: n, saved: 0, color: gType==="event" ? BRAND.coral : BRAND.aqua });
    onClose();
  }

  const overlay = { position:"fixed",inset:0,zIndex:300,display:"flex",alignItems:"flex-end",
    justifyContent:"center",background:"rgba(0,10,30,0.75)",backdropFilter:"blur(6px)" };
  const sheet = { width:"100%",maxWidth:420,background:BRAND.navyMid,
    borderRadius:"24px 24px 0 0",padding:"28px 22px 44px",
    border:`1px solid rgba(127,255,212,0.2)` };
  const inp = { width:"100%",background:"rgba(255,255,255,0.07)",
    border:"1px solid rgba(255,255,255,0.15)",borderRadius:10,
    padding:"11px 14px",color:BRAND.white,fontSize:15,marginBottom:12,
    fontFamily:"Lato,sans-serif",outline:"none",boxSizing:"border-box" };

  const pill = (active, col=BRAND.aqua) => ({
    padding:"8px 16px",borderRadius:20,
    border:`1px solid ${active ? col : "rgba(255,255,255,0.15)"}`,
    background:active ? `${col}22` : "rgba(255,255,255,0.05)",
    color:active ? col : "rgba(255,255,255,0.55)",
    fontWeight:700,fontSize:13,cursor:"pointer",
    fontFamily:"Lato,sans-serif",whiteSpace:"nowrap",
  });

  const nextBtn = (onClick, disabled=false) => ({
    width:"100%",background:disabled?"rgba(255,255,255,0.1)":`linear-gradient(135deg,${BRAND.ocean},${BRAND.aqua})`,
    border:"none",borderRadius:12,padding:"13px",color:disabled?"rgba(255,255,255,0.3)":BRAND.navy,
    fontWeight:900,fontSize:15,cursor:disabled?"default":"pointer",
    fontFamily:"Lato,sans-serif",opacity:disabled?0.5:1,
  });

  return (
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,color:BRAND.white,fontWeight:700}}>
            {step===1?"What are you saving for?":step===2?"Name your goal":"Set your goal amount"}
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:22,cursor:"pointer"}}>✕</button>
        </div>

        {step===1 && (
          <>
            <div style={{display:"flex",gap:10,marginBottom:20}}>
              {GOAL_PRESETS.map(p=>(
                <button key={p.type} onClick={()=>setGType(p.type)} style={{
                  ...pill(gType===p.type, p.type==="event"?BRAND.coral:BRAND.aqua),
                  flex:1,padding:"14px 10px",flexDirection:"column",display:"flex",alignItems:"center",gap:6,fontSize:14,
                }}>
                  <span style={{fontSize:26}}>{p.emoji}</span>{p.label}
                </button>
              ))}
            </div>
            {gType && (
              <>
                <div style={{color:"rgba(255,255,255,0.45)",fontSize:11,letterSpacing:1,textTransform:"uppercase",fontWeight:700,marginBottom:10}}>Quick pick</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:20}}>
                  {GOAL_PRESETS.find(p=>p.type===gType).options.map(o=>(
                    <button key={o} onClick={()=>setGName(o)} style={pill(gName===o, gType==="event"?BRAND.coral:BRAND.aqua)}>{o}</button>
                  ))}
                </div>
                <div style={{color:"rgba(255,255,255,0.35)",fontSize:12,marginBottom:8}}>Or type your own ↓</div>
                <input value={customName} onChange={e=>{setCustomName(e.target.value);setGName(e.target.value);}}
                  placeholder="Custom name..." style={inp}/>
                <button onClick={()=>gName&&setStep(2)} style={nextBtn(!gName)}>Next →</button>
              </>
            )}
          </>
        )}

        {step===2 && (
          <>
            <div style={{color:"rgba(255,255,255,0.45)",fontSize:11,letterSpacing:1,textTransform:"uppercase",fontWeight:700,marginBottom:10}}>Pick an emoji</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:18}}>
              {EMOJIS.map(e=>(
                <button key={e} onClick={()=>setGEmoji(e)} style={{
                  width:40,height:40,borderRadius:10,fontSize:20,cursor:"pointer",
                  background:gEmoji===e?"rgba(127,255,212,0.2)":"rgba(255,255,255,0.06)",
                  border:gEmoji===e?`1px solid ${BRAND.aqua}`:"1px solid rgba(255,255,255,0.1)",
                }}>{e}</button>
              ))}
            </div>
            <div style={{color:"rgba(255,255,255,0.45)",fontSize:11,letterSpacing:1,textTransform:"uppercase",fontWeight:700,marginBottom:8}}>Goal name</div>
            <input value={gName} onChange={e=>setGName(e.target.value)} placeholder="e.g. Girls Weekend Cabo" style={inp}/>
            <button onClick={()=>setStep(3)} style={nextBtn()}>Next →</button>
          </>
        )}

        {step===3 && (
          <>
            <div style={{color:"rgba(255,255,255,0.45)",fontSize:11,letterSpacing:1,textTransform:"uppercase",fontWeight:700,marginBottom:8}}>How much do you need?</div>
            <input value={gGoal} onChange={e=>setGGoal(e.target.value)} type="number"
              placeholder="$0" style={{...inp,fontSize:28,fontWeight:900,color:BRAND.aqua}}/>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:18}}>
              {[1500,2500,3500,5000,7500,10000].map(a=>(
                <button key={a} onClick={()=>setGGoal(String(a))} style={pill(Number(gGoal)===a)}>
                  ${a.toLocaleString()}
                </button>
              ))}
            </div>
            <button onClick={finish} style={nextBtn(!gGoal)}>Create Goal ✈️</button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Dollar Menu Sheet ────────────────────────────────────────
function DollarMenuSheet({ checkedHabits, bonusClaimed, totalBonus, checkedCount,
  onToggle, onClaim, onClose }) {
  return (
    <div style={{position:"fixed",inset:0,zIndex:150,display:"flex",alignItems:"flex-end",
      justifyContent:"center",background:"rgba(0,10,30,0.8)",backdropFilter:"blur(5px)"}}
      onClick={onClose}>
      <div style={{width:"100%",maxWidth:420,background:BRAND.navyMid,
        borderRadius:"24px 24px 0 0",padding:"28px 22px 48px",
        border:`1px solid rgba(255,107,74,0.2)`,animation:"slideUp 0.3s ease"}}
        onClick={e=>e.stopPropagation()}>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            {/* Coral accent mark */}
            <div style={{width:4,height:24,background:BRAND.coral,borderRadius:2}}/>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,color:BRAND.white,fontWeight:700}}>Dollar Menu 💵</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:22,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{color:"rgba(255,255,255,0.4)",fontSize:13,marginBottom:20,paddingLeft:14}}>
          Good habits earn real dollars toward your trip.
        </div>

        {WEEKLY_HABITS.map(h=>(
          <div key={h.id} onClick={()=>!bonusClaimed&&onToggle(h.id)}
            style={{display:"flex",alignItems:"center",justifyContent:"space-between",
              padding:"12px 14px",borderRadius:12,marginBottom:8,cursor:"pointer",
              background:checkedHabits[h.id]?"rgba(255,107,74,0.1)":"rgba(255,255,255,0.04)",
              border:checkedHabits[h.id]?`1px solid rgba(255,107,74,0.4)`:"1px solid rgba(255,255,255,0.07)",
              transition:"all 0.15s",opacity:bonusClaimed?0.55:1}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <span style={{fontSize:22}}>{h.emoji}</span>
              <span style={{color:BRAND.white,fontWeight:700,fontSize:14,fontFamily:"Lato,sans-serif"}}>{h.label}</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <span style={{color:BRAND.coral,fontWeight:900,fontSize:14,fontFamily:"Lato,sans-serif"}}>+${h.bonus}</span>
              <div style={{width:24,height:24,borderRadius:6,
                background:checkedHabits[h.id]?BRAND.coral:"rgba(255,255,255,0.08)",
                border:checkedHabits[h.id]?"none":"1px solid rgba(255,255,255,0.18)",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:13,color:BRAND.white,transition:"all 0.15s"}}>
                {checkedHabits[h.id]?"✓":""}
              </div>
            </div>
          </div>
        ))}

        <div style={{marginTop:18,padding:"16px",borderRadius:14,
          background:"linear-gradient(135deg,rgba(255,107,74,0.12),rgba(255,107,74,0.05))",
          border:`1px solid rgba(255,107,74,0.3)`,textAlign:"center"}}>
          <div style={{color:"rgba(255,255,255,0.45)",fontSize:11,letterSpacing:1,
            textTransform:"uppercase",fontWeight:700,marginBottom:6,fontFamily:"Lato,sans-serif"}}>
            {checkedCount} habit{checkedCount!==1?"s":""} completed this week
          </div>
          <div style={{fontSize:34,fontWeight:900,color:BRAND.coral,fontFamily:"'Playfair Display',serif"}}>
            ${totalBonus} bonus
          </div>
          <button onClick={onClaim} disabled={totalBonus===0||bonusClaimed}
            style={{marginTop:14,width:"100%",
              background:totalBonus>0&&!bonusClaimed
                ?`linear-gradient(135deg,${BRAND.coral},#ff8c6a)`
                :"rgba(255,255,255,0.08)",
              border:"none",borderRadius:12,padding:"13px",
              color:totalBonus>0&&!bonusClaimed?BRAND.white:"rgba(255,255,255,0.3)",
              fontWeight:900,fontSize:15,cursor:totalBonus>0&&!bonusClaimed?"pointer":"default",
              fontFamily:"Lato,sans-serif",transition:"all 0.2s"}}>
            {bonusClaimed?"✅ Claimed this week!":totalBonus>0?`Claim $${totalBonus} ✈️`:"Check habits to earn"}
          </button>
        </div>
        {!bonusClaimed && (
          <div style={{color:"rgba(255,255,255,0.25)",fontSize:11,textAlign:"center",marginTop:10,fontFamily:"Lato,sans-serif"}}>
            Resets every Sunday · Unclaimed bonuses don't roll over
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main App ─────────────────────────────────────────────────
export default function Step2Destinations() {
  const [tab, setTab]           = useState("dashboard");
  const [goals, setGoals]       = useState(DEFAULT_GOALS);
  const [activeGoalId, setActiveGoalId] = useState("g1");
  const [streak, setStreak]     = useState(9);
  const [history, setHistory]   = useState([
    { label:"Coffee shop pass",  amount:7,  date:"Today",     goalId:"g1" },
    { label:"DoorDash skipped",  amount:32, date:"Yesterday", goalId:"g1" },
    { label:"Manicure at home",  amount:55, date:"Mon",       goalId:"g2" },
  ]);
  const [swapFx, setSwapFx]       = useState(null);
  const [customAmt, setCustomAmt] = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const [pulse, setPulse]         = useState(false);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [checkedHabits, setCheckedHabits] = useState({});
  const [bonusClaimed, setBonusClaimed]   = useState(false);
  const [showDollarMenu, setShowDollarMenu] = useState(false);

  const activeGoal  = goals.find(g=>g.id===activeGoalId) || goals[0];
  const pct         = Math.min(100, Math.round((activeGoal.saved/activeGoal.goal)*100));
  const totalBonus  = WEEKLY_HABITS.filter(h=>checkedHabits[h.id]).reduce((s,h)=>s+h.bonus,0);
  const checkedCount= Object.values(checkedHabits).filter(Boolean).length;
  const accentColor = activeGoal.color || BRAND.aqua;

  function toggleHabit(id) { setCheckedHabits(p=>({...p,[id]:!p[id]})); }

  function claimBonus() {
    if (totalBonus===0||bonusClaimed) return;
    doSave({ label:`Weekly habit bonus (${checkedCount} habits)`, amount:totalBonus });
    setBonusClaimed(true);
    setShowDollarMenu(false);
  }

  function doSave(item) {
    setGoals(gs=>gs.map(g=>g.id===activeGoalId?{...g,saved:g.saved+item.amount}:g));
    setHistory(h=>[{...item,date:"Just now",goalId:activeGoalId},...h.slice(0,19)]);
    setSwapFx(item.amount);
    setPulse(true);
    setTimeout(()=>setPulse(false),900);
  }

  function doCustom() {
    const n=parseFloat(customAmt);
    if(!n||n<=0) return;
    doSave({label:customLabel||"Manual save",amount:n});
    setCustomAmt(""); setCustomLabel("");
  }

  function addGoal(newGoal) {
    setGoals(g=>[...g,newGoal]);
    setActiveGoalId(newGoal.id);
  }

  // Shared card style
  const card = {
    background:BRAND.glass, backdropFilter:"blur(14px)",
    borderRadius:20, border:`1px solid ${BRAND.glassBorder}`,
    padding:"20px 18px", marginBottom:14,
  };

  const tabStyle = (t) => ({
    flex:1, padding:"10px 0", border:"none", background:"none",
    color:tab===t?BRAND.aqua:"rgba(255,255,255,0.4)",
    fontSize:11, fontFamily:"Lato,sans-serif", fontWeight:700,
    letterSpacing:1, textTransform:"uppercase", cursor:"pointer",
    borderTop:tab===t?`2px solid ${BRAND.coral}`:"2px solid transparent",
    transition:"all 0.2s",
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Lato:wght@300;400;700;900&display=swap');
        @keyframes twinkle{0%,100%{opacity:0.15}50%{opacity:1}}
        @keyframes pulse-ring{0%{transform:scale(1);opacity:1}100%{transform:scale(1.2);opacity:0}}
        @keyframes slideUp{from{transform:translateY(24px);opacity:0}to{transform:translateY(0);opacity:1}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        *{box-sizing:border-box;margin:0;padding:0;}
        ::-webkit-scrollbar{width:3px}
        ::-webkit-scrollbar-thumb{background:rgba(127,255,212,0.3);border-radius:3px}
        input,select,textarea{outline:none;}
        button{transition:all 0.15s;}
      `}</style>

      <WaveBg/>
      {swapFx!==null && <SwapFx amount={swapFx} onDone={()=>setSwapFx(null)}/>}
      {showAddGoal && <AddGoalModal onAdd={addGoal} onClose={()=>setShowAddGoal(false)}/>}
      {showDollarMenu && (
        <DollarMenuSheet
          checkedHabits={checkedHabits} bonusClaimed={bonusClaimed}
          totalBonus={totalBonus} checkedCount={checkedCount}
          onToggle={toggleHabit} onClaim={claimBonus}
          onClose={()=>setShowDollarMenu(false)}/>
      )}

      <div style={{position:"relative",zIndex:1,maxWidth:420,margin:"0 auto",
        minHeight:"100dvh",display:"flex",flexDirection:"column",
        fontFamily:"Lato,sans-serif",paddingBottom:68}}>

        {/* ── Header ── */}
        <div style={{padding:"24px 20px 12px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <LogoMark size={40}/>
            <div>
              <div style={{fontSize:10,letterSpacing:3,color:BRAND.aqua,fontWeight:700,textTransform:"uppercase"}}>
                by Jumping Sunsets
              </div>
              <div style={{fontSize:22,fontWeight:900,color:BRAND.white,
                fontFamily:"'Playfair Display',serif",lineHeight:1.1}}>
                Step 2 Destinations
              </div>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <button onClick={()=>setShowDollarMenu(true)} style={{
              background:`rgba(255,107,74,0.15)`,border:`1px solid rgba(255,107,74,0.4)`,
              borderRadius:20,padding:"6px 12px",display:"flex",alignItems:"center",gap:5,
              cursor:"pointer",fontFamily:"Lato,sans-serif",
            }}>
              <span style={{fontSize:14}}>💵</span>
              <span style={{color:BRAND.coral,fontWeight:700,fontSize:12}}>Weekly</span>
            </button>
            <div style={{background:"rgba(255,140,0,0.15)",border:"1px solid rgba(255,140,0,0.35)",
              borderRadius:20,padding:"5px 11px",display:"flex",alignItems:"center",gap:5}}>
              <span style={{fontSize:15}}>🔥</span>
              <span style={{color:"#ffaa44",fontWeight:700,fontSize:14}}>{streak}</span>
            </div>
          </div>
        </div>

        {/* ── Coral divider ── */}
        <div style={{height:2,background:`linear-gradient(90deg,transparent,${BRAND.coral},transparent)`,
          opacity:0.4,margin:"0 20px 4px"}}/>

        <div style={{flex:1,overflowY:"auto",padding:"0 14px"}}>

          {/* ════ DASHBOARD ════ */}
          {tab==="dashboard" && (
            <div style={{animation:"fadeIn 0.35s ease"}}>

              {/* Goal selector */}
              <div style={{display:"flex",gap:8,margin:"12px 0",overflowX:"auto",paddingBottom:4}}>
                {goals.map(g=>(
                  <button key={g.id} onClick={()=>setActiveGoalId(g.id)} style={{
                    flexShrink:0,padding:"7px 14px",borderRadius:20,cursor:"pointer",
                    background:activeGoalId===g.id?`${g.color}22`:"rgba(255,255,255,0.05)",
                    border:`1px solid ${activeGoalId===g.id?g.color:"rgba(255,255,255,0.1)"}`,
                    color:activeGoalId===g.id?g.color:"rgba(255,255,255,0.55)",
                    fontWeight:700,fontSize:12,display:"flex",alignItems:"center",gap:6,
                    fontFamily:"Lato,sans-serif",whiteSpace:"nowrap",
                  }}>
                    {g.emoji} {g.name}
                  </button>
                ))}
                <button onClick={()=>setShowAddGoal(true)} style={{
                  flexShrink:0,padding:"7px 14px",borderRadius:20,cursor:"pointer",
                  background:"rgba(255,255,255,0.03)",border:"1px dashed rgba(255,255,255,0.18)",
                  color:"rgba(255,255,255,0.35)",fontWeight:700,fontSize:12,
                  fontFamily:"Lato,sans-serif",whiteSpace:"nowrap",
                }}>+ New Goal</button>
              </div>

              {/* Active goal card */}
              <div style={{...card,borderColor:activeGoal.type==="event"
                ?`rgba(255,107,74,0.3)`:`rgba(127,255,212,0.2)`}}>
                {activeGoal.type==="event" && (
                  <div style={{fontSize:10,letterSpacing:2,color:BRAND.coral,fontWeight:700,
                    textTransform:"uppercase",marginBottom:8,fontFamily:"Lato,sans-serif"}}>🎉 Event Fund</div>
                )}
                <div style={{display:"flex",alignItems:"center",gap:18}}>
                  <div style={{position:"relative"}}>
                    {pulse && <div style={{position:"absolute",inset:-4,borderRadius:"50%",
                      border:`3px solid ${accentColor}`,animation:"pulse-ring 0.9s ease-out forwards"}}/>}
                    <ProgressRing pct={pct} size={120} stroke={11} color={accentColor}>
                      <div style={{fontSize:22,fontWeight:900,color:BRAND.white,lineHeight:1}}>{pct}%</div>
                      <div style={{fontSize:10,color:"rgba(255,255,255,0.45)",marginTop:2}}>funded</div>
                    </ProgressRing>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:21,fontWeight:900,color:BRAND.white,
                      fontFamily:"'Playfair Display',serif",lineHeight:1.1}}>
                      {activeGoal.emoji} {activeGoal.name}
                    </div>
                    <div style={{display:"flex",alignItems:"baseline",gap:4,marginTop:8}}>
                      <span style={{fontSize:28,fontWeight:900,color:accentColor}}>
                        ${activeGoal.saved.toLocaleString()}
                      </span>
                      <span style={{color:"rgba(255,255,255,0.35)",fontSize:12}}>
                        / ${activeGoal.goal.toLocaleString()}
                      </span>
                    </div>
                    <div style={{marginTop:8,height:5,background:"rgba(255,255,255,0.08)",borderRadius:3,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${pct}%`,borderRadius:3,transition:"width 1s ease",
                        background:`linear-gradient(90deg,${BRAND.ocean},${accentColor})`}}/>
                    </div>
                    <div style={{color:"rgba(255,255,255,0.4)",fontSize:11,marginTop:5,fontFamily:"Lato,sans-serif"}}>
                      ${(activeGoal.goal-activeGoal.saved).toLocaleString()} left ✈️
                    </div>
                  </div>
                </div>
              </div>

              {/* Dollar Menu teaser — coral themed */}
              <button onClick={()=>setShowDollarMenu(true)} style={{
                ...card,width:"100%",cursor:"pointer",textAlign:"left",
                background:"linear-gradient(135deg,rgba(255,107,74,0.1),rgba(255,107,74,0.04))",
                border:`1px solid rgba(255,107,74,0.22)`,display:"flex",
                alignItems:"center",justifyContent:"space-between",
              }}>
                <div>
                  <div style={{fontSize:10,color:BRAND.coral,letterSpacing:2,
                    textTransform:"uppercase",fontWeight:700,marginBottom:4,fontFamily:"Lato,sans-serif"}}>
                    💵 Dollar Menu
                  </div>
                  <div style={{color:BRAND.white,fontSize:14,fontWeight:700,fontFamily:"Lato,sans-serif"}}>
                    {checkedCount>0
                      ?`${checkedCount} habit${checkedCount>1?"s":""} checked · $${totalBonus} ready`
                      :"Log good habits, earn bonus dollars"}
                  </div>
                  <div style={{color:"rgba(255,255,255,0.35)",fontSize:11,marginTop:3,fontFamily:"Lato,sans-serif"}}>
                    Cook home, skip delivery, stay active
                  </div>
                </div>
                <div style={{fontSize:20,marginLeft:10,color:BRAND.coral}}>
                  {bonusClaimed?"✅":"→"}
                </div>
              </button>

              {/* Quick Swap */}
              <div style={card}>
                <div style={{color:"rgba(255,255,255,0.5)",fontSize:10,letterSpacing:2,
                  textTransform:"uppercase",fontWeight:700,marginBottom:12,fontFamily:"Lato,sans-serif",
                  display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:3,height:14,background:BRAND.aqua,borderRadius:2}}/>
                  Quick Swap
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {SWAP_OPTIONS.slice(0,6).map((s,i)=>(
                    <button key={i} onClick={()=>doSave(s)} style={{
                      background:"rgba(127,255,212,0.08)",
                      border:`1px solid rgba(127,255,212,0.22)`,
                      borderRadius:20,padding:"7px 13px",color:BRAND.white,cursor:"pointer",
                      fontSize:12,fontWeight:700,display:"flex",alignItems:"center",gap:5,
                      fontFamily:"Lato,sans-serif",
                    }}>
                      {s.emoji} <span style={{color:BRAND.aqua}}>${s.amount}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Recent saves */}
              <div style={card}>
                <div style={{color:"rgba(255,255,255,0.5)",fontSize:10,letterSpacing:2,
                  textTransform:"uppercase",fontWeight:700,marginBottom:12,fontFamily:"Lato,sans-serif",
                  display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:3,height:14,background:BRAND.gold,borderRadius:2}}/>
                  Recent Saves
                </div>
                {history.slice(0,4).map((h,i)=>{
                  const g=goals.find(g=>g.id===h.goalId);
                  return (
                    <div key={i} style={{display:"flex",justifyContent:"space-between",
                      alignItems:"center",padding:"9px 0",
                      borderBottom:i<3?"1px solid rgba(255,255,255,0.05)":"none"}}>
                      <div>
                        <div style={{color:BRAND.white,fontSize:13,fontWeight:700,fontFamily:"Lato,sans-serif"}}>{h.label}</div>
                        <div style={{color:"rgba(255,255,255,0.35)",fontSize:11,fontFamily:"Lato,sans-serif"}}>
                          {h.date}{g?` · ${g.emoji} ${g.name}`:""}
                        </div>
                      </div>
                      <div style={{color:BRAND.aqua,fontWeight:900,fontSize:16,fontFamily:"Lato,sans-serif"}}>
                        +${Number(h.amount).toFixed(0)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Insight */}
              <div style={{...card,background:"linear-gradient(135deg,rgba(10,122,138,0.3),rgba(10,42,74,0.3))",
                border:`1px solid rgba(127,255,212,0.15)`}}>
                <div style={{fontSize:10,color:BRAND.aqua,letterSpacing:2,textTransform:"uppercase",
                  fontWeight:700,marginBottom:8,fontFamily:"Lato,sans-serif"}}>📊 This Week</div>
                <div style={{color:BRAND.white,fontSize:14,lineHeight:1.7,fontFamily:"Lato,sans-serif"}}>
                  You've redirected <span style={{color:BRAND.aqua,fontWeight:900}}>$94</span> from
                  habits into your fund. At this rate you'll hit your goal in{" "}
                  <span style={{color:BRAND.gold,fontWeight:700}}>11 weeks</span>. 🏖️
                </div>
              </div>
            </div>
          )}

          {/* ════ SWAP TAB ════ */}
          {tab==="swap" && (
            <div style={{animation:"fadeIn 0.35s ease"}}>
              <div style={{...card,marginTop:12}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,color:BRAND.white,fontWeight:700,marginBottom:4}}>
                  Swap It 🔄
                </div>
                <div style={{color:"rgba(255,255,255,0.45)",fontSize:13,marginBottom:16,fontFamily:"Lato,sans-serif"}}>
                  Log every skip and redirect it. Small choices, big trips.
                </div>
                <div style={{marginBottom:14}}>
                  <div style={{color:"rgba(255,255,255,0.45)",fontSize:10,letterSpacing:1,
                    textTransform:"uppercase",fontWeight:700,marginBottom:8,fontFamily:"Lato,sans-serif"}}>Saving to</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {goals.map(g=>(
                      <button key={g.id} onClick={()=>setActiveGoalId(g.id)} style={{
                        padding:"6px 14px",borderRadius:20,cursor:"pointer",
                        background:activeGoalId===g.id?`${g.color}22`:"rgba(255,255,255,0.05)",
                        border:`1px solid ${activeGoalId===g.id?g.color:"rgba(255,255,255,0.1)"}`,
                        color:activeGoalId===g.id?g.color:"rgba(255,255,255,0.45)",
                        fontWeight:700,fontSize:12,fontFamily:"Lato,sans-serif",
                      }}>{g.emoji} {g.name}</button>
                    ))}
                  </div>
                </div>
                {SWAP_OPTIONS.map((s,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                    padding:"13px 0",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
                    <div style={{display:"flex",alignItems:"center",gap:12}}>
                      <span style={{fontSize:24}}>{s.emoji}</span>
                      <div>
                        <div style={{color:BRAND.white,fontWeight:700,fontSize:14,fontFamily:"Lato,sans-serif"}}>{s.label}</div>
                        <div style={{color:"rgba(255,255,255,0.35)",fontSize:11,fontFamily:"Lato,sans-serif"}}>avg save</div>
                      </div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <span style={{color:BRAND.aqua,fontWeight:900,fontSize:16,fontFamily:"Lato,sans-serif"}}>${s.amount}</span>
                      <button onClick={()=>{doSave(s);setTab("dashboard");}} style={{
                        background:`linear-gradient(135deg,${BRAND.ocean},${BRAND.aqua})`,
                        border:"none",borderRadius:20,padding:"7px 16px",
                        color:BRAND.navy,fontWeight:900,fontSize:12,
                        cursor:"pointer",fontFamily:"Lato,sans-serif",
                      }}>+ Add</button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Custom amount */}
              <div style={card}>
                <div style={{color:"rgba(255,255,255,0.5)",fontSize:10,letterSpacing:2,
                  textTransform:"uppercase",fontWeight:700,marginBottom:12,fontFamily:"Lato,sans-serif",
                  display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:3,height:14,background:BRAND.coral,borderRadius:2}}/>
                  Custom Amount
                </div>
                <input value={customLabel} onChange={e=>setCustomLabel(e.target.value)}
                  placeholder="What did you skip?" style={{
                  width:"100%",background:"rgba(255,255,255,0.06)",
                  border:"1px solid rgba(255,255,255,0.12)",borderRadius:10,
                  padding:"10px 14px",color:BRAND.white,fontSize:14,marginBottom:10,
                  fontFamily:"Lato,sans-serif"}}/>
                <div style={{display:"flex",gap:10}}>
                  <input value={customAmt} onChange={e=>setCustomAmt(e.target.value)}
                    placeholder="$0.00" type="number" style={{
                    flex:1,background:"rgba(255,255,255,0.06)",
                    border:"1px solid rgba(255,255,255,0.12)",borderRadius:10,
                    padding:"10px 14px",color:BRAND.aqua,fontSize:18,fontWeight:900,
                    fontFamily:"Lato,sans-serif"}}/>
                  <button onClick={()=>{doCustom();setTab("dashboard");}} style={{
                    background:`linear-gradient(135deg,${BRAND.ocean},${BRAND.aqua})`,
                    border:"none",borderRadius:10,padding:"10px 22px",
                    color:BRAND.navy,fontWeight:900,fontSize:14,
                    cursor:"pointer",fontFamily:"Lato,sans-serif",
                  }}>Save ✈️</button>
                </div>
              </div>
            </div>
          )}

          {/* ════ CREW TAB ════ */}
          {tab==="crew" && (
            <div style={{animation:"fadeIn 0.35s ease"}}>
              <div style={{padding:"12px 0"}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,color:BRAND.white,fontWeight:700}}>
                  Your Crew 👯
                </div>
                <div style={{color:"rgba(255,255,255,0.45)",fontSize:13,marginTop:4,fontFamily:"Lato,sans-serif"}}>
                  Race to your next vacation together.
                </div>
              </div>

              {/* You */}
              <div style={{...card,border:`1px solid rgba(127,255,212,0.3)`}}>
                <div style={{display:"flex",alignItems:"center",gap:14}}>
                  <div style={{width:50,height:50,borderRadius:"50%",flexShrink:0,
                    background:`linear-gradient(135deg,${BRAND.ocean},${BRAND.aqua})`,
                    display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>🌺</div>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",justifyContent:"space-between"}}>
                      <div style={{color:BRAND.aqua,fontWeight:900,fontSize:15,fontFamily:"Lato,sans-serif"}}>You</div>
                      <div style={{color:BRAND.aqua,fontWeight:900,fontFamily:"Lato,sans-serif"}}>
                        ${activeGoal.saved.toLocaleString()}
                      </div>
                    </div>
                    <div style={{height:5,background:"rgba(255,255,255,0.08)",borderRadius:3,marginTop:7,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${pct}%`,
                        background:`linear-gradient(90deg,${BRAND.ocean},${BRAND.aqua})`,borderRadius:3}}/>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",marginTop:5}}>
                      <span style={{color:"rgba(255,255,255,0.35)",fontSize:11,fontFamily:"Lato,sans-serif"}}>🔥 {streak} day streak</span>
                      <span style={{color:"rgba(255,255,255,0.35)",fontSize:11,fontFamily:"Lato,sans-serif"}}>{pct}% to {activeGoal.name}</span>
                    </div>
                  </div>
                </div>
              </div>

              {FRIENDS.map((f,i)=>{
                const fp=Math.round((f.saved/f.goal)*100);
                return (
                  <div key={i} style={card}>
                    <div style={{display:"flex",alignItems:"center",gap:14}}>
                      <div style={{width:46,height:46,borderRadius:"50%",flexShrink:0,
                        background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.12)",
                        display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{f.avatar}</div>
                      <div style={{flex:1}}>
                        <div style={{display:"flex",justifyContent:"space-between"}}>
                          <div style={{color:BRAND.white,fontWeight:700,fontSize:14,fontFamily:"Lato,sans-serif"}}>{f.name}</div>
                          <div style={{color:"rgba(255,255,255,0.65)",fontWeight:700,fontFamily:"Lato,sans-serif"}}>
                            ${f.saved.toLocaleString()}
                          </div>
                        </div>
                        <div style={{height:4,background:"rgba(255,255,255,0.07)",borderRadius:3,marginTop:7,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${fp}%`,background:`${BRAND.ocean}99`,borderRadius:3}}/>
                        </div>
                        <div style={{display:"flex",justifyContent:"space-between",marginTop:5}}>
                          <span style={{color:"rgba(255,255,255,0.35)",fontSize:11,fontFamily:"Lato,sans-serif"}}>🔥 {f.streak}d streak</span>
                          <span style={{color:"rgba(255,255,255,0.35)",fontSize:11,fontFamily:"Lato,sans-serif"}}>{fp}% to {f.event}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div style={{...card,textAlign:"center",border:"1px dashed rgba(127,255,212,0.25)"}}>
                <div style={{fontSize:26,marginBottom:8}}>➕</div>
                <div style={{color:BRAND.aqua,fontWeight:700,fontSize:14,fontFamily:"Lato,sans-serif"}}>Invite to your Crew</div>
                <div style={{color:"rgba(255,255,255,0.35)",fontSize:12,marginTop:4,fontFamily:"Lato,sans-serif"}}>
                  Share your link and save together
                </div>
                <button style={{marginTop:14,
                  background:`linear-gradient(135deg,${BRAND.ocean},${BRAND.aqua})`,
                  border:"none",borderRadius:20,padding:"10px 28px",
                  color:BRAND.navy,fontWeight:900,fontSize:13,
                  cursor:"pointer",fontFamily:"Lato,sans-serif"}}>
                  Copy Invite Link 🔗
                </button>
              </div>
            </div>
          )}

          {/* ════ BADGES TAB ════ */}
          {tab==="badges" && (
            <div style={{animation:"fadeIn 0.35s ease"}}>
              <div style={{padding:"12px 0"}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,color:BRAND.white,fontWeight:700}}>
                  Badges & Streaks 🏅
                </div>
                <div style={{color:"rgba(255,255,255,0.45)",fontSize:13,marginTop:4,fontFamily:"Lato,sans-serif"}}>
                  Every skip earns you something.
                </div>
              </div>

              {/* Streak card */}
              <div style={{...card,
                background:"linear-gradient(135deg,rgba(255,107,74,0.18),rgba(255,107,74,0.06))",
                border:`1px solid rgba(255,107,74,0.3)`,textAlign:"center",paddingTop:28,paddingBottom:28}}>
                <div style={{fontSize:48}}>🔥</div>
                <div style={{fontSize:42,fontWeight:900,color:BRAND.coral,
                  fontFamily:"'Playfair Display',serif",marginTop:4}}>{streak}</div>
                <div style={{color:"rgba(255,255,255,0.55)",fontSize:13,marginTop:2,fontFamily:"Lato,sans-serif"}}>
                  Day Streak — Keep going!
                </div>
                <div style={{marginTop:16,display:"flex",justifyContent:"center",gap:8}}>
                  {["M","T","W","T","F","S","S"].map((d,i)=>(
                    <div key={i} style={{width:32,height:32,borderRadius:"50%",
                      background:i<(streak%7)?BRAND.coral:"rgba(255,255,255,0.07)",
                      border:`1px solid ${i<(streak%7)?BRAND.coral:"rgba(255,255,255,0.15)"}`,
                      display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,
                      color:i<(streak%7)?BRAND.white:"rgba(255,255,255,0.25)",
                      fontWeight:700,fontFamily:"Lato,sans-serif"}}>
                      {d}
                    </div>
                  ))}
                </div>
              </div>

              {/* Badges grid */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                {BADGES.map((b,i)=>(
                  <div key={i} style={{...card,textAlign:"center",padding:"18px 12px",
                    opacity:b.earned?1:0.38,filter:b.earned?"none":"grayscale(1)",position:"relative",
                    border:b.earned?`1px solid rgba(255,215,0,0.2)`:BRAND.glassBorder}}>
                    <div style={{fontSize:34}}>{b.emoji}</div>
                    <div style={{color:BRAND.white,fontWeight:700,fontSize:12,marginTop:8,fontFamily:"Lato,sans-serif"}}>{b.label}</div>
                    {b.earned && <div style={{position:"absolute",top:10,right:10,fontSize:11}}>✅</div>}
                    {!b.earned && <div style={{color:"rgba(255,255,255,0.25)",fontSize:10,marginTop:4,fontFamily:"Lato,sans-serif"}}>Locked</div>}
                  </div>
                ))}
              </div>

              {/* Lifetime stats */}
              <div style={{...card,marginTop:4}}>
                <div style={{color:"rgba(255,255,255,0.5)",fontSize:10,letterSpacing:2,
                  textTransform:"uppercase",fontWeight:700,marginBottom:14,fontFamily:"Lato,sans-serif",
                  display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:3,height:14,background:BRAND.gold,borderRadius:2}}/>
                  Lifetime Stats
                </div>
                {[
                  {label:"Total Redirected",val:`$${goals.reduce((s,g)=>s+g.saved,0).toLocaleString()}`,emoji:"💰"},
                  {label:"Skips Logged",    val:`${history.length+18}`,                                emoji:"🚫"},
                  {label:"Habits Claimed",  val:"$17",                                                 emoji:"💵"},
                  {label:"Longest Streak",  val:`${streak+4} days`,                                    emoji:"🔥"},
                ].map((s,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                    padding:"10px 0",borderBottom:i<3?"1px solid rgba(255,255,255,0.05)":"none"}}>
                    <div style={{display:"flex",gap:10,alignItems:"center"}}>
                      <span style={{fontSize:16}}>{s.emoji}</span>
                      <span style={{color:"rgba(255,255,255,0.55)",fontSize:13,fontFamily:"Lato,sans-serif"}}>{s.label}</span>
                    </div>
                    <span style={{color:BRAND.aqua,fontWeight:900,fontSize:14,fontFamily:"Lato,sans-serif"}}>{s.val}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Bottom Nav ── */}
        <div style={{position:"fixed",bottom:0,left:"50%",transform:"translateX(-50%)",
          width:"100%",maxWidth:420,
          background:"rgba(5,18,38,0.97)",backdropFilter:"blur(20px)",
          borderTop:`1px solid rgba(255,107,74,0.2)`,display:"flex"}}>
          {[
            {id:"dashboard",emoji:"🏝️",label:"Home"},
            {id:"swap",     emoji:"🔄",label:"Swap"},
            {id:"crew",     emoji:"👯",label:"Crew"},
            {id:"badges",   emoji:"🏅",label:"Badges"},
          ].map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)} style={tabStyle(t.id)}>
              <div style={{fontSize:19,marginBottom:2}}>{t.emoji}</div>
              {t.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
